import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { organizationService } from '../services/organization';
import { employeeService } from '../services/employee';
import {
  Organization,
  OrganizationRestaurant,
  Employee,
  CreateEmployeeRequest,
  EmployeeRole,
  AppRole,
} from '../types';
import { useForm } from 'react-hook-form';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { UiSelect } from '../components/UiSelect';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  ChevronDown,
  ChevronRight,
  Building2,
  UtensilsCrossed,
  Shield,
  KeyRound,
  Mail,
  Phone,
  Tag,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { PageSkeleton, EmptyState, ButtonLoader } from '../components/LoadingStates';

interface EmployeeFormData {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  mobile?: string;
  password?: string;
}

type RestaurantRoleState = {
  restaurantId: string;
  restaurantName: string;
  assigned: boolean;
  roles: EmployeeRole[];
};

interface RoleFormData { key: string; label: string; color: string; sortOrder: number; }

const ROLE_COLOR_OPTIONS: { value: string; label: string }[] = [
  { value: 'bg-blue-100 text-blue-700', label: 'Blue' },
  { value: 'bg-green-100 text-green-700', label: 'Green' },
  { value: 'bg-purple-100 text-purple-700', label: 'Purple' },
  { value: 'bg-orange-100 text-orange-700', label: 'Orange' },
  { value: 'bg-red-100 text-red-700', label: 'Red' },
  { value: 'bg-yellow-100 text-yellow-700', label: 'Yellow' },
  { value: 'bg-pink-100 text-pink-700', label: 'Pink' },
  { value: 'bg-teal-100 text-teal-700', label: 'Teal' },
  { value: 'bg-indigo-100 text-indigo-700', label: 'Indigo' },
  { value: 'bg-gray-100 text-gray-700', label: 'Gray' },
];

export const EmployeeManagement: React.FC<{ platform?: boolean }> = ({ platform = false }) => {
  const { currentTheme } = useTheme();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());

  // Modals
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [restaurantRoleStates, setRestaurantRoleStates] = useState<RestaurantRoleState[]>([]);

  // Tabs + role management
  const [activeTab, setActiveTab] = useState<'employees' | 'roles'>('employees');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<AppRole | null>(null);
  const roleForm = useForm<RoleFormData>({ defaultValues: { key: '', label: '', color: ROLE_COLOR_OPTIONS[0].value, sortOrder: 0 } });

  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'warning' | 'danger' | 'info';
    confirmText: string;
  }>({
    isOpen: false, title: '', message: '', onConfirm: () => {}, type: 'warning', confirmText: 'Confirm',
  });

  const form = useForm<EmployeeFormData>();

  // ── Queries ───────────────────────────────────────────────────────────────
  // Platform portal → all organizations. Regular org-admin → only their own org
  // (listing all orgs is restricted to the platform owner on the backend).
  const { data: allOrgs = [], isLoading: allOrgsLoading } = useQuery(
    ['organizations-emp'],
    organizationService.getOrganizations,
    { enabled: platform }
  );
  const { data: ownOrg, isLoading: ownOrgLoading } = useQuery(
    ['own-org-emp', user?.organizationCode],
    () => organizationService.getOrganizationByCode(user!.organizationCode!),
    { enabled: !platform && !!user?.organizationCode }
  );
  const organizations: Organization[] = platform ? allOrgs : (ownOrg ? [ownOrg] : []);
  const orgsLoading = platform ? allOrgsLoading : ownOrgLoading;

  const selectedOrgCode = organizations.find(o => o.id === selectedOrgId)?.code;

  const { data: roles = [] } = useQuery(
    ['org-roles-emp', selectedOrgCode],
    () => organizationService.getRolesByOrgCode(selectedOrgCode!),
    { enabled: !!selectedOrgCode }
  );

  const getRoleColor = (key: string) => roles.find(r => r.key === key)?.color || 'bg-gray-100 text-gray-700';
  const getRoleLabel = (key: string) => roles.find(r => r.key === key)?.label || key.replace('_', ' ');

  const { data: employees = [], isLoading: empsLoading } = useQuery(
    ['employees', selectedOrgId],
    () => employeeService.getEmployeesByOrganization(selectedOrgId),
    { enabled: !!selectedOrgId }
  );

  const { data: restaurants = [] } = useQuery(
    ['org-restaurants-emp', selectedOrgId],
    () => organizationService.getRestaurantsByOrganization(selectedOrgId),
    { enabled: !!selectedOrgId }
  );

  // Auto-select first org
  useEffect(() => {
    if (organizations.length > 0 && !selectedOrgId) {
      setSelectedOrgId(organizations[0].id);
    }
  }, [organizations, selectedOrgId]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createMutation = useMutation(
    (data: CreateEmployeeRequest) => employeeService.createEmployee(selectedOrgId, data),
    {
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to create employee'); },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<CreateEmployeeRequest> }) =>
      employeeService.updateEmployee(selectedOrgId, id, data),
    {
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to update employee'); },
    }
  );

  // Apply the restaurant-role selections for an employee: assign where roles are
  // chosen, remove a restaurant that was previously assigned and is now off.
  const applyRestaurantRoles = async (employeeId: string, previous?: Employee['restaurantRoles']) => {
    for (const state of restaurantRoleStates) {
      if (state.assigned && state.roles.length > 0) {
        await employeeService.assignRestaurantRole(selectedOrgId, employeeId, state.restaurantId, state.roles);
      } else if (!state.assigned && previous?.some(rr => rr.restaurantId === state.restaurantId)) {
        await employeeService.removeRestaurantRole(selectedOrgId, employeeId, state.restaurantId);
      }
    }
  };

  const deleteMutation = useMutation(
    (id: string) => employeeService.deleteEmployee(selectedOrgId, id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['employees', selectedOrgId]);
        toast.success('Employee deleted');
      },
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to delete employee'); },
    }
  );

  const toggleStatusMutation = useMutation(
    (id: string) => employeeService.toggleEmployeeStatus(selectedOrgId, id),
    {
      onSuccess: () => { queryClient.invalidateQueries(['employees', selectedOrgId]); },
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to update status'); },
    }
  );

  // ── Role management mutations ─────────────────────────────────────────────
  const closeRoleModal = () => {
    setShowRoleModal(false);
    setEditingRole(null);
    roleForm.reset({ key: '', label: '', color: ROLE_COLOR_OPTIONS[0].value, sortOrder: 0 });
  };

  const createRoleMutation = useMutation(
    (data: Omit<AppRole, 'id' | 'organizationCode'>) => organizationService.createRole(selectedOrgCode!, data),
    {
      onSuccess: () => { queryClient.invalidateQueries(['org-roles-emp', selectedOrgCode]); closeRoleModal(); toast.success('Role created'); },
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to create role'); },
    }
  );
  const updateRoleMutation = useMutation(
    ({ id, data }: { id: string; data: Omit<AppRole, 'id' | 'organizationCode'> }) => organizationService.updateRole(id, data),
    {
      onSuccess: () => { queryClient.invalidateQueries(['org-roles-emp', selectedOrgCode]); closeRoleModal(); toast.success('Role updated'); },
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to update role'); },
    }
  );
  const deleteRoleMutation = useMutation(
    (id: string) => organizationService.deleteRole(id),
    {
      onSuccess: () => { queryClient.invalidateQueries(['org-roles-emp', selectedOrgCode]); toast.success('Role deleted'); },
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to delete role'); },
    }
  );

  const openCreateRole = () => {
    setEditingRole(null);
    roleForm.reset({ key: '', label: '', color: ROLE_COLOR_OPTIONS[0].value, sortOrder: roles.length });
    setShowRoleModal(true);
  };
  const openEditRole = (role: AppRole) => {
    setEditingRole(role);
    roleForm.reset({ key: role.key, label: role.label, color: role.color || ROLE_COLOR_OPTIONS[0].value, sortOrder: role.sortOrder });
    setShowRoleModal(true);
  };
  const onRoleSubmit = (d: RoleFormData) => {
    if (editingRole?.id) updateRoleMutation.mutate({ id: editingRole.id, data: d });
    else createRoleMutation.mutate(d);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const toggleExpand = (empId: string) => {
    setExpandedEmployees(prev => {
      const next = new Set(prev);
      next.has(empId) ? next.delete(empId) : next.add(empId);
      return next;
    });
  };

  const openCreateEmployee = () => {
    setEditingEmployee(null);
    form.reset();
    const states: RestaurantRoleState[] = restaurants.map(r => ({
      restaurantId: r.id,
      restaurantName: r.name,
      assigned: false,
      roles: [],
    }));
    setRestaurantRoleStates(states);
    setShowEmployeeModal(true);
  };

  const openEditEmployee = (emp: Employee) => {
    setEditingEmployee(emp);
    form.reset({
      firstName: emp.firstName ?? '',
      middleName: emp.middleName ?? '',
      lastName: emp.lastName ?? '',
      email: emp.email,
      mobile: emp.mobile,
      password: '',
    });
    const states: RestaurantRoleState[] = restaurants.map(r => {
      const existing = emp.restaurantRoles?.find(rr => rr.restaurantId === r.id);
      return {
        restaurantId: r.id,
        restaurantName: r.name,
        assigned: !!existing,
        roles: existing ? (existing.roles as EmployeeRole[]) : [],
      };
    });
    setRestaurantRoleStates(states);
    setShowEmployeeModal(true);
  };

  const closeEmployeeModal = () => {
    setShowEmployeeModal(false);
    setEditingEmployee(null);
    form.reset();
  };

  const onEmployeeSubmit = async (data: EmployeeFormData) => {
    // Assigning at least one restaurant + role is mandatory (when the org has any).
    if (restaurants.length > 0) {
      if (!restaurantRoleStates.some(s => s.assigned && s.roles.length > 0)) {
        toast.error('Assign at least one restaurant and a role');
        return;
      }
      if (restaurantRoleStates.some(s => s.assigned && s.roles.length === 0)) {
        toast.error('Select at least one role for each assigned restaurant');
        return;
      }
    }
    try {
      if (editingEmployee) {
        const payload: Partial<CreateEmployeeRequest> = {
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          mobile: data.mobile,
        };
        if (data.password) payload.password = data.password;
        await updateMutation.mutateAsync({ id: editingEmployee.id, data: payload });
        await applyRestaurantRoles(editingEmployee.id, editingEmployee.restaurantRoles);
        queryClient.invalidateQueries(['employees', selectedOrgId]);
        closeEmployeeModal();
        toast.success('Employee updated successfully');
      } else {
        const created = await createMutation.mutateAsync({
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          email: data.email,
          mobile: data.mobile,
          password: data.password!,
          organizationId: selectedOrgId,
          organizationCode: selectedOrgCode,
        });
        await applyRestaurantRoles(created.id);
        queryClient.invalidateQueries(['employees', selectedOrgId]);
        closeEmployeeModal();
        toast.success('Employee created successfully');
      }
    } catch {
      // error toast already surfaced by the mutation's onError
    }
  };

  const toggleRestaurantAssign = (restaurantId: string) => {
    setRestaurantRoleStates(prev =>
      prev.map(s =>
        s.restaurantId === restaurantId
          ? { ...s, assigned: !s.assigned, roles: s.assigned ? [] : s.roles }
          : s
      )
    );
  };

  const toggleRole = (restaurantId: string, role: EmployeeRole) => {
    setRestaurantRoleStates(prev =>
      prev.map(s => {
        if (s.restaurantId !== restaurantId) return s;
        const roles = s.roles.includes(role)
          ? s.roles.filter(r => r !== role)
          : [...s.roles, role];
        return { ...s, roles };
      })
    );
  };

  const confirmDelete = (emp: Employee) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Employee',
      message: `Are you sure you want to delete "${getEmpDisplayName(emp)}"? All their restaurant role assignments will also be removed.`,
      onConfirm: () => {
        deleteMutation.mutate(emp.id);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      },
      type: 'danger',
      confirmText: 'Delete',
    });
  };

  const getRestaurantName = (restaurantId: string): string => {
    return restaurants.find(r => r.id === restaurantId)?.name || restaurantId;
  };

  const getEmpDisplayName = (emp: Employee): string => {
    const full = [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(' ');
    return full || emp.name || '—';
  };

  const filteredEmployees = employees.filter(emp =>
    getEmpDisplayName(emp).toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOrg = organizations.find(o => o.id === selectedOrgId);
  const isModalLoading = createMutation.isLoading || updateMutation.isLoading;
  // Restaurant + role assignment is required whenever the org has restaurants.
  const rolesValid = restaurants.length === 0
    || (restaurantRoleStates.some(s => s.assigned && s.roles.length > 0)
        && !restaurantRoleStates.some(s => s.assigned && s.roles.length === 0));

  if (orgsLoading) return <PageSkeleton type="grid" />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3.5 mb-4 sm:mb-0">
          <div className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0" style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}` }}>
            <Users className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Employee Management</h1>
            <p style={{ color: currentTheme.colors.text.secondary }}>
              Manage employees and their restaurant role assignments
            </p>
          </div>
        </div>
        {selectedOrgId && (
          <button
            onClick={activeTab === 'employees' ? openCreateEmployee : openCreateRole}
            className="inline-flex items-center px-4 py-2 text-white rounded-lg btn-primary transition-colors"
            style={{ backgroundColor: currentTheme.colors.primary[600] }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
          >
            <Plus className="h-5 w-5 mr-2" />
            {activeTab === 'employees' ? 'Add Employee' : 'Add Role'}
          </button>
        )}
      </div>

      {/* Organization Selector — only the platform owner picks an org; a regular
          org-admin is locked to their own organization. */}
      {platform && (
        <div className="mb-6 max-w-sm">
          <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>
            Select Organization
          </label>
          <UiSelect
            value={selectedOrgId}
            onChange={v => { setSelectedOrgId(v); setExpandedEmployees(new Set()); }}
            placeholder="-- Select Organization --"
            options={[{ value: '', label: '-- Select Organization --' }, ...organizations.map(org => ({ value: org.id, label: `${org.name} (${org.code})` }))]}
          />
        </div>
      )}

      {/* Stats bar */}
      {selectedOrg && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-lg mb-5 border"
          style={{ backgroundColor: currentTheme.colors.primary[50], borderColor: currentTheme.colors.primary[200] }}
        >
          <Building2 className="h-5 w-5 flex-shrink-0" style={{ color: currentTheme.colors.primary[600] }} />
          <span className="font-semibold" style={{ color: currentTheme.colors.primary[700] }}>{selectedOrg.name}</span>
          <span className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
            · {employees.length} employee{employees.length !== 1 ? 's' : ''}
            · {restaurants.length} restaurant{restaurants.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Content */}
      {!selectedOrgId ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Building2 className="h-12 w-12 mb-3" style={{ color: currentTheme.colors.text.secondary }} />
          <p className="text-lg font-medium" style={{ color: currentTheme.colors.text.secondary }}>
            Select an organization to manage employees
          </p>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex border-b mb-5" style={{ borderColor: currentTheme.colors.border.primary }}>
            {(['employees', 'roles'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className="px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors capitalize flex items-center gap-2"
                style={activeTab === tab
                  ? { color: currentTheme.colors.primary[600], borderColor: currentTheme.colors.primary[600] }
                  : { color: currentTheme.colors.text.secondary, borderColor: 'transparent' }}>
                {tab === 'employees' ? <Users className="h-4 w-4" /> : <Tag className="h-4 w-4" />}
                {tab === 'employees' ? `Employees (${employees.length})` : `Roles (${roles.length})`}
              </button>
            ))}
          </div>

          {activeTab === 'employees' && (
            <>
              {/* Search */}
              <div className="relative max-w-sm mb-5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                  style={{ borderColor: currentTheme.colors.border.primary }}
                />
              </div>

              {empsLoading ? (
                <PageSkeleton type="grid" />
              ) : filteredEmployees.length === 0 ? (
        <EmptyState
          icon={<Users className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No Employees Found"
          description={searchTerm ? 'Try adjusting your search.' : 'Get started by adding your first employee.'}
          action={!searchTerm ? (
            <button
              onClick={openCreateEmployee}
              className="inline-flex items-center px-4 py-2 text-white rounded-lg btn-primary"
              style={{ backgroundColor: currentTheme.colors.primary[600] }}
            >
              <Plus className="h-5 w-5 mr-2" /> Add First Employee
            </button>
          ) : undefined}
        />
      ) : (
        <div className="space-y-3">
          {filteredEmployees.map(emp => {
            const isExpanded = expandedEmployees.has(emp.id);
            const empRoles = emp.restaurantRoles || [];

            return (
              <div
                key={emp.id}
                className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] overflow-hidden"
                style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: '#fff' }}
              >
                {/* Employee Row */}
                <div className="flex items-center justify-between px-5 py-4"
                  style={{ backgroundColor: currentTheme.colors.background.secondary || '#fafafa' }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <button
                      onClick={() => toggleExpand(emp.id)}
                      className="flex-shrink-0 p-1 rounded"
                      style={{ color: currentTheme.colors.primary[600] }}
                    >
                      {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                    </button>

                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0"
                      style={{ backgroundColor: currentTheme.colors.primary[100], color: currentTheme.colors.primary[700] }}>
                      {(emp.firstName || emp.name || '?').charAt(0).toUpperCase()}
                    </div>

                    <div className="min-w-0">
                      <p className="font-semibold truncate" style={{ color: currentTheme.colors.text.primary }}>
                        {getEmpDisplayName(emp)}
                      </p>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        <span className="flex items-center gap-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                          <Mail className="h-3 w-3" /> {emp.email}
                        </span>
                        {emp.mobile && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                            <Phone className="h-3 w-3" /> {emp.mobile}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    <span className={clsx('px-2.5 py-0.5 rounded-full text-xs font-medium',
                      emp.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600')}>
                      {emp.active ? 'Active' : 'Inactive'}
                    </span>

                    {isExpanded && (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        {empRoles.length} restaurant{empRoles.length !== 1 ? 's' : ''}
                      </span>
                    )}


                    <button onClick={() => openEditEmployee(emp)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium"
                      style={{ backgroundColor: currentTheme.colors.primary[50], color: currentTheme.colors.primary[700],
                        border: `1px solid ${currentTheme.colors.primary[200]}` }}
                      title="Edit employee & roles">
                      <Edit className="h-3.5 w-3.5" /> Edit
                    </button>

                    <button onClick={() => toggleStatusMutation.mutate(emp.id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                      style={{
                        backgroundColor: emp.active ? '#fef2f2' : '#f0fdf4',
                        color: emp.active ? '#dc2626' : '#16a34a',
                        border: `1px solid ${emp.active ? '#fecaca' : '#bbf7d0'}`
                      }}
                      title={emp.active ? 'Deactivate' : 'Activate'}>
                      {emp.active ? 'Deactivate' : 'Activate'}
                    </button>

                    <button onClick={() => confirmDelete(emp)}
                      className="p-2 rounded-lg bg-red-50 text-red-600" title="Delete Employee">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded: Restaurant Role Tree */}
                {isExpanded && (
                  <div className="px-5 py-3 bg-white">
                    {empRoles.length === 0 ? (
                      <div className="flex items-center gap-3 py-3 pl-8">
                        <span className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                          No restaurant roles assigned.
                        </span>
                        <button onClick={() => openEditEmployee(emp)}
                          className="inline-flex items-center gap-1 text-sm font-medium"
                          style={{ color: currentTheme.colors.primary[600] }}>
                          <Shield className="h-4 w-4" /> Assign Roles
                        </button>
                      </div>
                    ) : (
                      empRoles.map((rr, index) => {
                        const isLast = index === empRoles.length - 1;
                        return (
                          <div key={rr.restaurantId} className="flex items-start">
                            <div className="flex flex-col items-center mr-3 mt-1" style={{ width: 24 }}>
                              <div className="w-px" style={{ height: 12, backgroundColor: currentTheme.colors.border.primary }} />
                              <div className="flex items-center" style={{ height: 20 }}>
                                <div className="w-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }} />
                              </div>
                              {!isLast && (
                                <div className="w-px flex-1" style={{ minHeight: 12, backgroundColor: currentTheme.colors.border.primary }} />
                              )}
                            </div>

                            <div className="flex-1 flex items-center flex-wrap gap-2 px-4 py-3 mb-1 rounded-lg border"
                              style={{ borderColor: currentTheme.colors.border.primary }}>
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="p-1.5 rounded-md flex-shrink-0" style={{ backgroundColor: '#f0fdf4' }}>
                                  <UtensilsCrossed className="h-4 w-4 text-green-600" />
                                </div>
                                <span className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                                  {getRestaurantName(rr.restaurantId)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 flex-wrap ml-2">
                                {rr.roles.map(role => (
                                  <span key={role} className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',
                                    getRoleColor(role))}>
                                    {getRoleLabel(role)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
              )}
            </>
          )}

          {activeTab === 'roles' && (
            <div className="max-w-2xl">
              {/* Role list */}
              {roles.length === 0 ? (
                <EmptyState
                  icon={<Tag className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
                  title="No roles yet"
                  description="Create roles to assign to employees per restaurant."
                  action={(
                    <button onClick={openCreateRole} className="inline-flex items-center px-4 py-2 text-white rounded-lg btn-primary" style={{ backgroundColor: currentTheme.colors.primary[600] }}>
                      <Plus className="h-5 w-5 mr-2" /> Add Role
                    </button>
                  )}
                />
              ) : (
                <div className="space-y-2">
                  {roles.map(role => (
                    <div key={role.key} className="flex items-center justify-between px-4 py-3 rounded-xl border" style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: '#fff' }}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={clsx('px-2.5 py-0.5 rounded-full text-xs font-medium', role.color || 'bg-gray-100 text-gray-700')}>{role.label}</span>
                        <span className="font-mono text-xs truncate" style={{ color: currentTheme.colors.text.secondary }}>{role.key}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => openEditRole(role)} className="p-2 rounded-lg" style={{ color: currentTheme.colors.primary[600], backgroundColor: currentTheme.colors.primary[50] }} title="Edit">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => setConfirmationModal({
                          isOpen: true, title: 'Delete Role', type: 'danger', confirmText: 'Delete',
                          message: `Delete role "${role.label}"? Employees assigned this role will lose it.`,
                          onConfirm: () => { deleteRoleMutation.mutate(role.id!); setConfirmationModal(prev => ({ ...prev, isOpen: false })); },
                        })} className="p-2 rounded-lg bg-red-50 text-red-600" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Add/Edit Employee Modal */}
      {showEmployeeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Users className="h-5 w-5" style={{ color: currentTheme.colors.primary[600] }} />
                {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
              </h2>
              <button onClick={closeEmployeeModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            {selectedOrg && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4 text-sm"
                style={{ backgroundColor: currentTheme.colors.primary[50], color: currentTheme.colors.primary[700] }}>
                <Building2 className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium">{selectedOrg.name}</span>
              </div>
            )}

            <form onSubmit={form.handleSubmit(onEmployeeSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    {...form.register('firstName', { required: 'First name is required' })}
                    type="text" placeholder="e.g., RAHUL"
                    className="ui-input"
                  />
                  {form.formState.errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    {...form.register('lastName', { required: 'Last name is required' })}
                    type="text" placeholder="e.g., NAYAK"
                    className="ui-input"
                  />
                  {form.formState.errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.lastName.message}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                <input
                  {...form.register('middleName')}
                  type="text" placeholder="e.g., KUMAR (optional)"
                  className="ui-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (Login Username) {!editingEmployee && '*'}
                </label>
                <input
                  {...form.register('email', { required: !editingEmployee ? 'Email is required' : false })}
                  type="email" placeholder="employee@example.com"
                  disabled={!!editingEmployee}
                  className="ui-input disabled:bg-gray-50 disabled:text-gray-400"
                />
                {form.formState.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.email.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    {...form.register('mobile')}
                    type="text" placeholder="9876543210"
                    className="ui-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <KeyRound className="h-3.5 w-3.5" />
                    {editingEmployee ? 'New Password' : 'Password *'}
                  </label>
                  <input
                    {...form.register('password', { required: !editingEmployee ? 'Password is required' : false })}
                    type="password" placeholder={editingEmployee ? 'Leave blank to keep' : 'Min 6 characters'}
                    className="ui-input"
                  />
                  {form.formState.errors.password && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.password.message}</p>
                  )}
                </div>
              </div>

              {/* Restaurant Role Assignments — shown on both Add and Edit */}
              {restaurants.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Shield className="h-3.5 w-3.5" style={{ color: currentTheme.colors.primary[600] }} />
                    Restaurant &amp; Roles <span className="text-red-500">*</span>
                  </label>
                  {!rolesValid && (
                    <p className="mb-2 text-xs text-amber-600">Assign at least one restaurant and select a role for it.</p>
                  )}
                  <div className="space-y-2 max-h-52 overflow-y-auto border rounded-lg p-2"
                    style={{ borderColor: currentTheme.colors.border.primary }}>
                    {restaurantRoleStates.map(state => (
                      <div key={state.restaurantId}
                        className="border rounded-lg p-3 transition-colors"
                        style={{
                          borderColor: state.assigned ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                          backgroundColor: state.assigned ? currentTheme.colors.primary[50] : 'white',
                        }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <UtensilsCrossed className="h-3.5 w-3.5 text-green-600" />
                            <span className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                              {state.restaurantName}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleRestaurantAssign(state.restaurantId)}
                            className="text-xs px-2.5 py-1 rounded-md font-medium transition-colors"
                            style={state.assigned
                              ? { backgroundColor: currentTheme.colors.primary[600], color: 'white' }
                              : { backgroundColor: '#f3f4f6', color: '#6b7280' }}
                          >
                            {state.assigned ? 'Assigned' : 'Assign'}
                          </button>
                        </div>
                        {state.assigned && (
                          roles.length === 0 ? (
                            <p className="mt-2 text-xs text-amber-600">No roles configured. Add roles in Organization → Roles tab first.</p>
                          ) : (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {roles.map(role => (
                                <button
                                  key={role.key}
                                  type="button"
                                  onClick={() => toggleRole(state.restaurantId, role.key as EmployeeRole)}
                                  className={clsx(
                                    'px-2 py-0.5 rounded-full text-xs font-medium border transition-all',
                                    state.roles.includes(role.key as EmployeeRole)
                                      ? clsx(role.color, 'border-transparent')
                                      : 'bg-gray-50 text-gray-400 border-gray-200'
                                  )}
                                >
                                  {role.label}
                                </button>
                              ))}
                            </div>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeEmployeeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
                <button type="submit" disabled={isModalLoading || !rolesValid}
                  className="px-4 py-2 text-white rounded-lg btn-primary transition-colors disabled:opacity-50 flex items-center"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  onMouseEnter={e => !isModalLoading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                  onMouseLeave={e => !isModalLoading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}>
                  {isModalLoading
                    ? <ButtonLoader text={editingEmployee ? 'Updating...' : 'Creating...'} />
                    : editingEmployee ? 'Update Employee' : 'Create Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Role Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Tag className="h-5 w-5" style={{ color: currentTheme.colors.primary[600] }} />
                {editingRole ? 'Edit Role' : 'Add New Role'}
              </h2>
              <button onClick={closeRoleModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            {selectedOrg && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4 text-sm"
                style={{ backgroundColor: currentTheme.colors.primary[50], color: currentTheme.colors.primary[700] }}>
                <Building2 className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium">{selectedOrg.name}</span>
              </div>
            )}

            <form onSubmit={roleForm.handleSubmit(onRoleSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Key *</label>
                  <input {...roleForm.register('key', { required: 'Required', pattern: { value: /^[A-Z0-9_]+$/, message: 'UPPERCASE & underscores only' } })}
                    placeholder="e.g. SUPERVISOR" className="ui-input font-mono text-sm" />
                  {roleForm.formState.errors.key && <p className="mt-1 text-sm text-red-600">{roleForm.formState.errors.key.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
                  <input {...roleForm.register('label', { required: 'Required' })} placeholder="e.g. Supervisor" className="ui-input" />
                  {roleForm.formState.errors.label && <p className="mt-1 text-sm text-red-600">{roleForm.formState.errors.label.message}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <div className="grid grid-cols-5 gap-2">
                  {ROLE_COLOR_OPTIONS.map(opt => (
                    <label key={opt.value} className="cursor-pointer">
                      <input type="radio" {...roleForm.register('color')} value={opt.value} className="sr-only" />
                      <span className={clsx('block px-2 py-1 rounded-full text-xs font-medium text-center border-2 transition-all', opt.value,
                        roleForm.watch('color') === opt.value ? 'border-gray-600 scale-105' : 'border-transparent')}>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input {...roleForm.register('sortOrder', { valueAsNumber: true })} type="number" min={0} className="ui-input" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeRoleModal} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="submit" disabled={createRoleMutation.isLoading || updateRoleMutation.isLoading}
                  className="px-4 py-2 text-white rounded-lg btn-primary disabled:opacity-50 flex items-center"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}>
                  {(createRoleMutation.isLoading || updateRoleMutation.isLoading)
                    ? <ButtonLoader text="Saving..." />
                    : editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        type={confirmationModal.type}
        isLoading={deleteMutation.isLoading || deleteRoleMutation.isLoading}
      />
    </div>
  );
};
